import { useEffect, useRef, useState } from "react";
import { useGetUserDetailsMutation } from "redux/api/authApi";
import { ttsLanguageFor } from "../utils/ttsLanguage";

type AudioMap = Record<string, string>;

//  NEW
type PrefetchEntry = {
  chunks: string[];
  urls: (string | null | undefined)[];
};

const PREFETCH_COUNT = 3;
const TARGET_WORDS_PER_CHUNK = 20;

export const useTTSStream = (courseVoiceName?: any) => {
  const [isMuted, setIsMuted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioMapRef = useRef<AudioMap>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioInstancesRef = useRef<HTMLAudioElement[]>([]);

  const generationRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);


  // Persistent cross-play prefetch cache — NOT cleared by stopAll(),
  // so the "next" lesson's audio can be generated in the background
  // while the "current" module/lesson is still playing.


  const prefetchStoreRef = useRef<Record<string, PrefetchEntry>>({});

  const [userDetails, setUserDetails] = useState<any>();
  const [getUserDetailsMutation] = useGetUserDetailsMutation();
  const useremail = localStorage.getItem('user_email') || '';

  const [isProcessingAudio, setIsProcessingAudio] = useState(false);

  //const DEFAULT_VOICE = userDetails?.digitial_twin_voice ? userDetails?.digitial_twin_voice : "default.wav";
  const DEFAULT_VOICE = courseVoiceName ? courseVoiceName : "default";


  // ─────────────────────────────────────────────
  // MUTE SYNC
  // ─────────────────────────────────────────────
  useEffect(() => {
    audioInstancesRef.current.forEach((a) => {
      try {
        a.muted = isMuted;
      } catch { }
    });

    if (audioRef.current) {
      try {
        audioRef.current.muted = isMuted;
      } catch { }
    }
  }, [isMuted]);

  // ─────────────────────────────────────────────
  // CLEANUP ON UNMOUNT
  // ─────────────────────────────────────────────
  useEffect(() => {
    return () => {
      stopAll();

      // NEW — clean up anything that was prefetched but never consumed
      Object.values(prefetchStoreRef.current).forEach((entry) => {
        entry.urls.forEach((url) => {
          if (url?.startsWith("blob:")) {
            try { URL.revokeObjectURL(url); } catch {}
          }
        });
      });
      prefetchStoreRef.current = {};
    };
  }, []);

  // ─────────────────────────────────────────────
  // SPLIT TEXT
  // ─────────────────────────────────────────────
  // const splitText = (text: string): string[] => {
  //   if (!text) return [];

  //   text = text
  //     .replace(/\*+/g, " ")
  //     .replace(/#+/g, " ")
  //     .replace(/_+/g, " ")
  //     .replace(/`+/g, " ")
  //     .replace(/\s+/g, " ")
  //     .trim();

  //   const sentences =
  //     text.match(/[^.!?]+[.!?]+/g) || [text];

  //   const chunks: string[] = [];

  //   let current = "";

  //   for (const sent of sentences) {
  //     const trimmed = sent.trim();

  //     if (!trimmed) continue;

  //     const combinedWords = (
  //       current +
  //       " " +
  //       trimmed
  //     ).split(/\s+/).length;

  //     if (
  //       current &&
  //       combinedWords > TARGET_WORDS_PER_CHUNK
  //     ) {
  //       chunks.push(current.trim());

  //       current = trimmed;
  //     } else {
  //       current = current
  //         ? current + " " + trimmed
  //         : trimmed;
  //     }
  //   }

  //   if (current.trim()) {
  //     chunks.push(current.trim());
  //   }

  //   return chunks;
  // };

  // Chinese / Japanese: no spaces between words and sentences end with 。！？,
  // so the word-based splitter below would send a whole lesson as ONE chunk
  // (URL too long -> HTTP 414). Split on CJK punctuation and by characters.
  const CJK_RE = /[぀-ヿ一-鿿가-힯]/;
  const CJK_MAX_CHARS = 80;

  const splitCjk = (text: string): string[] => {
    const sentences = text.match(/[^。！？!?.]+[。！？!?.]*/g) || [text];
    const pieces: string[] = [];
    for (let s of sentences) {
      s = s.trim();
      if (!s) continue;
      if (s.length <= CJK_MAX_CHARS) { pieces.push(s); continue; }
      // long sentence: split at commas, then hard-cut
      let buf = "";
      for (const part of s.split(/(?<=[、，,；;：:])/)) {
        let p = part;
        while (p.length > CJK_MAX_CHARS) {
          if (buf) { pieces.push(buf); buf = ""; }
          pieces.push(p.slice(0, CJK_MAX_CHARS));
          p = p.slice(CJK_MAX_CHARS);
        }
        if ((buf + p).length > CJK_MAX_CHARS) { if (buf) pieces.push(buf); buf = p; }
        else buf += p;
      }
      if (buf) pieces.push(buf);
    }
    // merge short sentences up to the limit
    const chunks: string[] = [];
    let current = "";
    for (const p of pieces) {
      if (current && (current + p).length > CJK_MAX_CHARS) { chunks.push(current); current = p; }
      else current += p;
    }
    if (current.trim()) chunks.push(current.trim());
    return chunks;
  };

  const splitText = (text: string): string[] => {
    if (!text) return [];

    if (CJK_RE.test(text)) {
      const cleaned = text.replace(/[*#_`]+/g, " ").replace(/\s+/g, " ").trim();
      return cleaned ? splitCjk(cleaned) : [];
    }

    text = text
        .replace(/\*+/g, " ")
        .replace(/#+/g, " ")
        .replace(/_+/g, " ")
        .replace(/`+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    if (!text) return [];

    // FIXED — the old regex silently dropped any trailing text that
    // didn't end in . ! or ? — now we explicitly recover it.
    const matched = text.match(/[^.!?]+[.!?]+/g) || [];
    const matchedLength = matched.join("").length;
    const leftover = text.slice(matchedLength).trim();

    const sentences = leftover ? [...matched, leftover] : matched;
    if (sentences.length === 0) sentences.push(text);

    const chunks: string[] = [];
    let current = "";

    for (const sent of sentences) {
        const trimmed = sent.trim();

        if (!trimmed) continue;

        const combinedWords = (current + " " + trimmed).split(/\s+/).length;

        if (current && combinedWords > TARGET_WORDS_PER_CHUNK) {
            chunks.push(current.trim());
            current = trimmed;
        } else {
            current = current ? current + " " + trimmed : trimmed;
        }
    }

    if (current.trim()) {
        chunks.push(current.trim());
    }

    return chunks;
};

  // ─────────────────────────────────────────────
  // STOP ALL
  // ─────────────────────────────────────────────
  const stopAll = () => {
    generationRef.current++;

    abortControllerRef.current?.abort();

    setIsProcessingAudio(false);

    // cleanup blob urls
    Object.values(audioMapRef.current).forEach(
      (url) => {
        try {
          if (url?.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        } catch { }
      }
    );

    audioMapRef.current = {};

    audioInstancesRef.current.forEach((a) => {
      try {
        a.pause();
        a.currentTime = 0;
        a.onended = null;
        a.onerror = null;
        // a.onloadedmetadata = null;
        a.oncanplaythrough = null;
        a.src = "";
      } catch { }
    });

    audioInstancesRef.current = [];

    if (audioRef.current) {
      try {
        audioRef.current.pause();
      } catch { }

      audioRef.current = null;
    }

    setIsPlaying(false);
    setCurrentIndex(0);
  };

  const stop = () => stopAll();

  const fetchData = async () => {
    try {

      const response = await getUserDetailsMutation({ email: useremail, filter: '', start_date: '', end_date: '' });

      if ('data' in response) {
        const details = response.data.data;
        setUserDetails(details);
        console.log('userdetails from view course audio streaming =>', details);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchChunkAudio = async (
    text: string,
    voice: string
  ): Promise<string | null> => {
    try {
      // text goes in the body: in the URL, long (esp. Japanese/Chinese) text hits HTTP 414
      const res = await fetch(
        `${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak_stream`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, voice, language: ttsLanguageFor(text) }),
        }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      if (!blob || blob.size === 0) return null;

      return URL.createObjectURL(blob);
    } catch (err: any) {
      console.error("Prefetch chunk fetch failed", err);
      return null;
    }
  };


  // ─────────────────────────────────────────────
  // PREFETCH TEXT (background, non-blocking)
  // ─────────────────────────────────────────────
  const prefetchText = (
    key: string,
    text: string,
    voice: string = DEFAULT_VOICE
  ) => {
    if (!key || !text) return;
    if (prefetchStoreRef.current[key]) return; // already running/done

    const chunks = splitText(text);
    if (!chunks.length){
      setIsProcessingAudio(false);
      return;
    } 

    const urls: (string | null | undefined)[] = new Array(chunks.length);

    // Register immediately so a second call with the same key
    // (e.g. an effect re-firing) doesn't start a duplicate run.
    prefetchStoreRef.current[key] = { chunks, urls };

    (async () => {
      for (let i = 0; i < chunks.length; i++) {
        if (!prefetchStoreRef.current[key]) return; // cache was cleared/consumed elsewhere
        urls[i] = await fetchChunkAudio(chunks[i], voice);
      }
    })();
  };


  // ─────────────────────────────────────────────
  // PLAY TEXT
  // ─────────────────────────────────────────────
  const playText = async (
    text: string,
    voice: string = DEFAULT_VOICE,
    onChunkPlayed?: (
    chunkIndex: number,
    totalChunks: number
  ) => void,
  onChunkStart?: (chunkIndex: number, totalChunks: number, chunkText: string) => void ,
  cacheKey?: string   //  NEW
  ) => {
    stopAll();

    const myGeneration =
      generationRef.current;

    abortControllerRef.current =
      new AbortController();

    const signal =
      abortControllerRef.current.signal;

    const chunks = splitText(text);

    if (!chunks.length){
      setIsProcessingAudio(false);
      return;
    } 

    setIsPlaying(true);
    setIsProcessingAudio(true);

    //  NEW — reuse a background-prefetched cache if the text matches
    const cachedEntry = cacheKey ? prefetchStoreRef.current[cacheKey] : undefined;
    const usingPrefetch =
      !!cachedEntry &&
      cachedEntry.chunks.length === chunks.length &&
      cachedEntry.chunks.every((c, idx) => c === chunks[idx]);

    // const audioUrls: (
    //   | string
    //   | null
    //   | undefined
    // )[] = new Array(chunks.length);
    const audioUrls: (string | null | undefined)[] = usingPrefetch
    ? cachedEntry!.urls
    : new Array(chunks.length);

    let fetchIndex = 0;

    // ─────────────────────────────────────────
    // FETCH SINGLE CHUNK
    // ─────────────────────────────────────────
    const fetchChunk = async (
      index: number
    ) => {
      if (index >= chunks.length) return;

      try {
        // text goes in the body: in the URL, long (esp. Japanese/Chinese) text hits HTTP 414
        const res = await fetch(
          `${process.env.REACT_APP_TEXT_TO_SPEECH_SERVICE}/speak_stream`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              text: chunks[index],
              voice,
              language: ttsLanguageFor(chunks[index]),
            }),
            signal,
          }
        );

        console.log("TTS response:", {
  status: res.status,
  ok: res.ok,
  contentType: res.headers.get("content-type"),
  contentLength: res.headers.get("content-length"),
});

        if (!res.ok) {
          throw new Error(
            `HTTP ${res.status}`
          );
        }

        const blob = await res.blob();

        console.log("TTS blob:", {
  size: blob.size,
  type: blob.type,
});

        if (!blob || blob.size === 0) {
          audioUrls[index] = null;
          return;
        }

        const blobUrl =
          URL.createObjectURL(blob);

        audioUrls[index] = blobUrl;

        audioMapRef.current[
          `chunk-${index}`
        ] = blobUrl;
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error(
            "Chunk fetch failed",
            err
          );
        }

        audioUrls[index] = null;
      }
    };

    // ─────────────────────────────────────────
    // INITIAL PREFETCH
    // ─────────────────────────────────────────
    // const starters = [];

    // for (
    //   let i = 0;
    //   i <
    //   Math.min(
    //     PREFETCH_COUNT,
    //     chunks.length
    //   );
    //   i++
    // ) {
    //   starters.push(fetchChunk(fetchIndex++));
    // }

    // await Promise.all(starters);
    if (!usingPrefetch) {
      const starters = [];
      for (
        let i = 0;
        i < Math.min(PREFETCH_COUNT, chunks.length);
        i++
      ) {
        starters.push(fetchChunk(fetchIndex++));
      }
      await Promise.all(starters);
    }

    // ─────────────────────────────────────────
    // PLAY SEQUENTIALLY
    // ─────────────────────────────────────────
    for (let i = 0; i < chunks.length; i++) {
      if (
        myGeneration !==
        generationRef.current
      ) {
        break;
      }

      //setCurrentIndex(i);

      // // prefetch next chunk
      // if (fetchIndex < chunks.length) {
      //   fetchChunk(fetchIndex++);
      // }

      // CHANGED — skip playText's own fetch-ahead when a background
      // prefetch is already filling this array for us
      if (!usingPrefetch && fetchIndex < chunks.length) {
        fetchChunk(fetchIndex++);
      }

       if (audioUrls[i] === undefined) {
          setIsProcessingAudio(true);
      }

      // wait until chunk available
      while (audioUrls[i] === undefined) {
        if (
          myGeneration !==
          generationRef.current
        ) {
          setIsPlaying(false);
          setIsProcessingAudio(false);
          return;
        }

        await new Promise((r) =>
          setTimeout(r, 50)
        );
      }

      if (audioUrls[i] === null) {
        continue;
      }

      // // NEW — this cache entry has been fully consumed
      // if (cacheKey && usingPrefetch) {
      //   delete prefetchStoreRef.current[cacheKey];
      // }


      // if (myGeneration === generationRef.current) {
      //   setIsPlaying(false);
      // }

      /* await new Promise<void>((resolve) => {
        const audio = new Audio(
          audioUrls[i]!
        );

        audioRef.current = audio;

        audioInstancesRef.current.push(
          audio
        );

        audio.muted = isMuted;
        audio.preload = "auto";

        let settled = false;

        const finish = () => {
          if (settled) return;

          settled = true;

          audio.onended = null;
          audio.onerror = null;
          // audio.onloadedmetadata = null;
          audio.oncanplaythrough = null;

          try {
            audio.pause();
          } catch { }

          if (audio.src?.startsWith("blob:")) {
            URL.revokeObjectURL(audio.src);
          }

          resolve();
        };

        audio.onended = () => {
          finish();
        };

        audio.onerror = (e) => {
          console.error(
            "Audio playback failed",
            e
          );

          finish();
        };

        audio.oncanplaythrough = async () => {
          if (
            myGeneration !==
            generationRef.current
          ) {
            finish();
            return;
          }

          try {
            await audio.play();
          } catch (err) {
            console.error(
              "Audio play failed",
              err
            );

            finish();
          }
        };
      }); */

      /* const chunkPlayedSuccessfully = await new Promise<boolean>(
  (resolve) => {
    const audio = new Audio(
      audioUrls[i]!
    );

    audioRef.current = audio;

    audioInstancesRef.current.push(audio);

    audio.muted = isMuted;
    audio.preload = "auto";

    let settled = false;

    const finish = (success: boolean) => {
      if (settled) return;

      settled = true;

      audio.onended = null;
      audio.onerror = null;
      audio.oncanplaythrough = null;

      try {
        audio.pause();
      } catch {}

      if (audio.src?.startsWith("blob:")) {
        URL.revokeObjectURL(audio.src);
      }

      resolve(success);
    };

    audio.onended = () => {
      finish(true);
    };

    audio.onerror = (e) => {
      console.error(
        "Audio playback failed",
        e
      );

      finish(false);
    };

    audio.oncanplaythrough = async () => {
      if (
        myGeneration !==
        generationRef.current
      ) {
        finish(false);
        return;
      }

      try {
        await audio.play();
      } catch (err) {
        console.error(
          "Audio play failed",
          err
        );

        finish(false);
      }
    };
  }
); */
const chunkPlayedSuccessfully = await new Promise<boolean>((resolve) => {
  const audio = new Audio();

  audioRef.current = audio;
  audioInstancesRef.current.push(audio);

  audio.muted = isMuted;
  audio.preload = "auto";

  let settled = false;

  const cleanup = () => {
    audio.onended = null;
    audio.onerror = null;
    audio.oncanplay = null;
    audio.onloadeddata = null;
    audio.onloadedmetadata = null;
  };

  const finish = (success: boolean) => {
    if (settled) return;

    settled = true;
    cleanup();

    try {
      audio.pause();
    } catch {}

    if (audio.src?.startsWith("blob:")) {
      URL.revokeObjectURL(audio.src);
    }

    resolve(success);
  };

  audio.onerror = (event) => {
    console.error("Audio playback failed:", {
      event,
      src: audio.src,
      readyState: audio.readyState,
      networkState: audio.networkState,
      error: audio.error,
    });

    finish(false);
  };

  audio.onended = () => {
    finish(true);
  };

  const startPlayback = async () => {
    if (settled) return;

    if (myGeneration !== generationRef.current) {
      finish(false);
      return;
    }

    try {
      await audio.play();

      // ✅ move index/caption sync here — this is the true "audio started" moment
      setCurrentIndex(i);
      onChunkStart?.(i, chunks.length, chunks[i]);
      setIsProcessingAudio(false);

      console.log("TTS audio playback started:", {
        chunkIndex: i,
        duration: audio.duration,
        readyState: audio.readyState,
      });
    } catch (error) {
      console.error("TTS audio.play() failed:", {
        chunkIndex: i,
        error,
        name: (error as any)?.name,
        message: (error as any)?.message,
      });

      finish(false);
    }
  };

  audio.onloadeddata = () => {
    startPlayback();
  };

  audio.oncanplay = () => {
    startPlayback();
  };

  audio.src = audioUrls[i]!;

  // Important for the new TTS endpoint
  audio.load();

  // If browser already has enough data, don't wait forever for an event.
  if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    startPlayback();
  }
});

     if (chunkPlayedSuccessfully && onChunkPlayed) {
        onChunkPlayed(i, chunks.length);
      }

}

  //  ADD here — cache is only safe to clear once every chunk has actually played
    if (cacheKey && usingPrefetch) {
      delete prefetchStoreRef.current[cacheKey];
    }

    if (
      myGeneration ===
      generationRef.current
    ) {
      setIsPlaying(false);
      setIsProcessingAudio(false);
    }
  };

  // ─────────────────────────────────────────────
  // PAUSE
  // ─────────────────────────────────────────────
  const pause = () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();

        setIsPlaying(false);
      } catch { }
    }
  };

  // ─────────────────────────────────────────────
  // RESUME
  // ─────────────────────────────────────────────
  const resume = async () => {
    if (audioRef.current) {
      try {
        await audioRef.current.play();

        setIsPlaying(true);
      } catch (err) {
        console.error(
          "Resume failed",
          err
        );
      }
    }
  };

  // ─────────────────────────────────────────────
  // SEEK
  // ─────────────────────────────────────────────
  const seek = async (
    text: string,
    _index: number
  ) => {
    stopAll();

    await playText(text);
  };

  

  return {
    playText,
    prefetchText,
    pause,
    resume,
    stop,
    stopAll,
    seek,
    isPlaying,
    isProcessingAudio,
    currentIndex,
    audioRef,
    isMuted,
    setIsMuted,
  };
};

export default useTTSStream;